<script setup>
import {ref, reactive, computed} from 'vue';
import axios from 'axios';

const step = ref('info'); // 'info', 'login', 'register'
const loading = ref(false);
const error = ref('');
const success = ref('');
const registrationPending = ref(false);

const formData = reactive({
    code: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    password_confirmation: ''
});

const validationErrors = reactive({
    code: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    password_confirmation: ''
});

const isFormValid = computed(() => {
    if (step.value === 'info') {
        return (
            formData.code.trim() !== '' &&
            formData.email.trim() !== '' &&
            formData.first_name.trim() !== '' &&
            formData.last_name.trim() !== ''
        );
    } else if (step.value === 'login') {
        return formData.email.trim() !== '' && formData.password.trim() !== '';
    } else if (step.value === 'register') {
        return (
            formData.first_name.trim() !== '' &&
            formData.last_name.trim() !== '' &&
            formData.email.trim() !== '' &&
            formData.password.trim() !== '' &&
            formData.password_confirmation.trim() !== ''
        );
    }
    return false;
});

const showRequestError = (err) => {
    const errors = err.response?.status === 422 ? (err.response?.data?.errors || {}) : {};
    Object.keys(validationErrors).forEach(key => {
        const messages = errors[key];
        validationErrors[key] = Array.isArray(messages) ? messages[0] : (typeof messages === 'string' ? messages : '');
    });
    const status = err.response?.status;
    if (Object.values(validationErrors).some(Boolean)) {
        error.value = 'Vérifiez les champs indiqués, puis ressaisissez et confirmez votre mot de passe.';
    } else if (status === 419) {
        error.value = 'Votre session a expiré. Copiez vos informations et le code de la carte avant de recharger la page, puis réessayez.';
    } else if (status === 429) {
        error.value = 'Trop de tentatives rapprochées. Patientez quelques minutes avant de réessayer. Si le blocage persiste, contactez-nous.';
    } else if (status === 401) {
        error.value = 'Connexion ou activation refusée. Vérifiez vos identifiants et le code de la carte. Utilisez « Mot de passe oublié » si nécessaire, ou contactez-nous pour vérifier la carte.';
    } else if (status === 403) {
        error.value = 'L’accès a été refusé. Copiez vos informations avant de recharger la page. Si le blocage persiste, contactez-nous.';
    } else if ([404, 405].includes(status)) {
        error.value = 'Ce formulaire est indisponible. Copiez vos informations, puis ouvrez à nouveau la page d’activation. Si le problème persiste, contactez-nous.';
    } else if ([408, 504].includes(status) || ['ECONNABORTED', 'ETIMEDOUT'].includes(err.code)) {
        error.value = 'Le site n’a pas répondu à temps. Connectez-vous pour vérifier votre compte et votre solde avant de recommencer. Si le résultat reste incertain, contactez-nous avec le code de la carte.';
    } else if (status >= 500) {
        error.value = 'Le service rencontre un problème. Réessayez dans quelques minutes en vérifiant votre compte et votre solde avant de recommencer. Si le problème persiste, contactez-nous.';
    } else if (!err.response) {
        error.value = 'La connexion au site a été interrompue. Vérifiez votre connexion, puis votre compte et votre solde avant de recommencer. Si le résultat reste incertain, contactez-nous.';
    } else {
        error.value = 'Nous n’avons pas pu confirmer le résultat de votre demande. Vérifiez votre compte et votre solde avant de recommencer. Si vous restez bloqué, contactez-nous avec le code de la carte.';
    }
};

const continueActivation = () => {
    registrationPending.value = false;
    success.value = '';
    error.value = '';
    step.value = 'login';
};

const validateCode = async () => {
    if (loading.value) return;
    // Validate all required fields
    let isValid = true;

    if (!formData.code) {
        validationErrors.code = 'Le code est requis';
        isValid = false;
    } else {
        validationErrors.code = '';
    }

    if (!formData.email) {
        validationErrors.email = 'L\'email est requis';
        isValid = false;
    } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            validationErrors.email = 'L\'email n\'est pas valide';
            isValid = false;
        } else {
            validationErrors.email = '';
        }
    }

    if (!formData.first_name) {
        validationErrors.first_name = 'Le prénom est requis';
        isValid = false;
    } else {
        validationErrors.first_name = '';
    }

    if (!formData.last_name) {
        validationErrors.last_name = 'Le nom est requis';
        isValid = false;
    } else {
        validationErrors.last_name = '';
    }

    if (!isValid) {
        return;
    }

    loading.value = true;
    error.value = '';
    success.value = '';

    try {
        await axios.get('/sanctum/csrf-cookie');
        const response = await axios.post('/api/gift-card/activate', {
            code: formData.code,
            email: formData.email,
            first_name: formData.first_name,
            last_name: formData.last_name
        });

        // If user is authenticated, the card is activated
        if (!response.data.data.hasOwnProperty('authenticated')) {
            success.value = response.data.message;
            return;
        }

        // If code is valid but user is not authenticated
        if (response.data.data.valid && !response.data.data.authenticated) {
            step.value = 'login';
        }
    } catch (err) {
        showRequestError(err);
    } finally {
        loading.value = false;
    }
};

const loginAndActivate = async () => {
    if (loading.value) return;
    if (!formData.email) {
        validationErrors.email = 'L\'email est requis';
        return;
    }

    if (!formData.password) {
        validationErrors.password = 'Le mot de passe est requis';
        return;
    }

    validationErrors.email = '';
    validationErrors.password = '';
    loading.value = true;
    error.value = '';
    success.value = '';

    try {
        const response = await axios.post('/api/gift-card/login-and-activate', {
            email: formData.email,
            password: formData.password,
            code: formData.code
        });

        success.value = response.data.message;
        step.value = 'success';
    } catch (err) {
        showRequestError(err);
    } finally {
        loading.value = false;
    }
};

const registerAndActivate = async () => {
    if (loading.value) return;
    // Validate form
    let isValid = true;

    if (!formData.first_name) {
        validationErrors.first_name = 'Le prénom est requis';
        isValid = false;
    } else {
        validationErrors.first_name = '';
    }

    if (!formData.last_name) {
        validationErrors.last_name = 'Le nom est requis';
        isValid = false;
    } else {
        validationErrors.last_name = '';
    }

    if (!formData.email) {
        validationErrors.email = 'L\'email est requis';
        isValid = false;
    } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            validationErrors.email = 'L\'email n\'est pas valide';
            isValid = false;
        } else {
            validationErrors.email = '';
        }
    }

    if (!formData.password) {
        validationErrors.password = 'Le mot de passe est requis';
        isValid = false;
    } else if (formData.password.length < 12) {
        validationErrors.password = 'Le mot de passe doit contenir au moins 12 caractères';
        isValid = false;
    } else {
        validationErrors.password = '';
    }

    if (formData.password !== formData.password_confirmation) {
        validationErrors.password_confirmation = 'Les mots de passe ne correspondent pas';
        isValid = false;
    } else {
        validationErrors.password_confirmation = '';
    }

    if (!isValid) {
        return;
    }

    loading.value = true;
    error.value = '';
    success.value = '';

    try {
        const response = await axios.post('/api/gift-card/register-and-activate', {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            password_confirmation: formData.password_confirmation,
            password: formData.password,
            code: formData.code
        }, {timeout: 30000});

        if (response.status !== 202 || response.data?.data?.authentication_required !== true || typeof response.data?.message !== 'string') {
            showRequestError({response});
            return;
        }
        registrationPending.value = true;
        success.value = response.data.message;
        step.value = 'success';
    } catch (err) {
        showRequestError(err);
    } finally {
        loading.value = false;
        formData.password = '';
        formData.password_confirmation = '';
    }
};

const reset = () => {
    registrationPending.value = false;
    step.value = 'info';
    error.value = '';
    success.value = '';
    formData.code = '';
    formData.email = '';
    formData.password = '';
    formData.first_name = '';
    formData.last_name = '';
    formData.password_confirmation = '';
    Object.keys(validationErrors).forEach(key => {
        validationErrors[key] = '';
    });
};
</script>

<template>
    <div class="container py-4">
        <div class="card shadow mx-auto" style="max-width: 550px;">
            <div class="card-body p-4">
                <h1 class="card-title text-center mb-4 fw-bold">Activer une carte cadeau</h1>

                <div v-if="error" class="alert alert-danger d-flex align-items-center" role="alert">
                    <i class="bi bi-exclamation-circle-fill me-2"></i>
                    {{ error }}
                </div>

                <div v-if="success" class="alert alert-success d-flex align-items-center" role="alert">
                    <i class="bi bi-check-circle-fill me-2"></i>
                    {{ success }}
                    <div class="mt-3 w-100">
                        <button
                            @click="registrationPending ? continueActivation() : reset()"
                            class="btn btn-primary"
                        >
                            {{ registrationPending ? 'Se connecter pour activer cette carte' : 'Activer une autre carte' }}
                        </button>
                    </div>
                </div>

                <div v-if="!success">
                    <!-- Step indicator -->
                    <div class="progress mb-4" style="height: 4px;">
                        <div class="progress-bar" role="progressbar"
                             :style="{ width: step === 'info' ? '50%' : '100%' }"
                             :aria-valuenow="step === 'info' ? 50 : 100"
                             aria-valuemin="0"
                             aria-valuemax="100">
                        </div>
                    </div>

                    <div class="d-flex justify-content-between mb-4">
            <span class="badge rounded-pill"
                  :class="{ 'bg-primary': step === 'info', 'bg-success': step === 'login' || step === 'register' }">
              1
            </span>
                        <span class="badge rounded-pill"
                              :class="{ 'bg-secondary': step === 'info', 'bg-primary': step === 'login' || step === 'register' }">
              2
            </span>
                    </div>

                    <!-- Step 1: Enter Gift Card Info -->
                    <div v-if="step === 'info'">
                        <div class="mb-3">
                            <label for="code" class="form-label">
                                Code de la carte cadeau
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-key-fill"></i>
                </span>
                                <input
                                    id="code"
                                    v-model="formData.code"
                                    type="text"
                                    class="form-control"
                                    placeholder="Entrez le code de votre carte cadeau"
                                />
                            </div>
                            <div v-if="validationErrors.code" class="form-text text-danger">
                                {{ validationErrors.code }}
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="email" class="form-label">
                                Email
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-envelope-fill"></i>
                </span>
                                <input
                                    id="email"
                                    v-model="formData.email"
                                    type="email"
                                    class="form-control"
                                    placeholder="Entrez votre email"
                                />
                            </div>
                            <div v-if="validationErrors.email" class="form-text text-danger">
                                {{ validationErrors.email }}
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="first_name" class="form-label">
                                Prénom
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-person-fill"></i>
                </span>
                                <input
                                    id="first_name"
                                    v-model="formData.first_name"
                                    type="text"
                                    class="form-control"
                                    placeholder="Entrez votre prénom"
                                />
                            </div>
                            <div v-if="validationErrors.first_name" class="form-text text-danger">
                                {{ validationErrors.first_name }}
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="last_name" class="form-label">
                                Nom
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-person-fill"></i>
                </span>
                                <input
                                    id="last_name"
                                    v-model="formData.last_name"
                                    type="text"
                                    class="form-control"
                                    placeholder="Entrez votre nom"
                                />
                            </div>
                            <div v-if="validationErrors.last_name" class="form-text text-danger">
                                {{ validationErrors.last_name }}
                            </div>
                        </div>

                        <div class="d-grid gap-2 mt-4">
                            <button
                                @click="validateCode"
                                :disabled="loading || !isFormValid"
                                class="btn btn-primary"
                            >
                <span v-if="loading">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Chargement...
                </span>
                                <span v-else>
                  <i class="bi bi-check-lg me-2"></i>
                  Vérifier
                </span>
                            </button>
                        </div>
                    </div>

                    <!-- Step 2: Login -->
                    <div v-if="step === 'login'" class="fade show">
                        <button type="button" class="btn btn-link mb-3" :disabled="loading" @click="step = 'register'; error = ''">Créer un compte</button>
                        <div class="mb-3">
                            <label for="login-email" class="form-label">
                                Email
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-envelope-fill"></i>
                </span>
                                <input
                                    id="login-email"
                                    v-model="formData.email"
                                    type="email"
                                    class="form-control bg-light"
                                    readonly
                                />
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="password" class="form-label">
                                Mot de passe
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-lock-fill"></i>
                </span>
                                <input
                                    id="password"
                                    v-model="formData.password"
                                    type="password"
                                    class="form-control"
                                    placeholder="Entrez votre mot de passe"
                                />
                            </div>
                            <div v-if="validationErrors.password" class="form-text text-danger">
                                {{ validationErrors.password }}
                            </div>
                        </div>

                        <div class="d-grid gap-2 mt-4">
                            <button
                                @click="loginAndActivate"
                                :disabled="loading || !formData.password"
                                class="btn btn-primary"
                            >
                <span v-if="loading">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Chargement...
                </span>
                                <span v-else>
                  <i class="bi bi-box-arrow-in-right me-2"></i>
                  Se connecter et activer
                </span>
                            </button>
                        </div>
                    </div>

                    <!-- Step 3: Register -->
                    <div v-if="step === 'register'" class="fade show">
                        <div class="mb-3">
                            <label for="first_name" class="form-label">
                                Prénom
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-person-fill"></i>
                </span>
                                <input
                                    id="first_name"
                                    v-model="formData.first_name"
                                    type="text"
                                    class="form-control"
                                    placeholder="Entrez votre prénom"
                                />
                            </div>
                            <div v-if="validationErrors.first_name" class="form-text text-danger">
                                {{ validationErrors.first_name }}
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="last_name" class="form-label">
                                Nom
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-person-fill"></i>
                </span>
                                <input
                                    id="last_name"
                                    v-model="formData.last_name"
                                    type="text"
                                    class="form-control"
                                    placeholder="Entrez votre nom"
                                />
                            </div>
                            <div v-if="validationErrors.last_name" class="form-text text-danger">
                                {{ validationErrors.last_name }}
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="register-email" class="form-label">
                                Email
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-envelope-fill"></i>
                </span>
                                <input
                                    id="register-email"
                                    v-model="formData.email"
                                    type="email"
                                    class="form-control bg-light"
                                    readonly
                                />
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="register-password" class="form-label">
                                Mot de passe
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-lock-fill"></i>
                </span>
                                <input
                                    id="register-password"
                                    minlength="12"
                                    maxlength="255"
                                    autocomplete="new-password"
                                    aria-describedby="register-password-help"
                                    v-model="formData.password"
                                    type="password"
                                    class="form-control"
                                    placeholder="Entrez votre mot de passe"
                                />
                            </div>
                            <div id="register-password-help" class="form-text">12 caractères minimum.</div>
                            <div v-if="validationErrors.password" class="form-text text-danger">
                                {{ validationErrors.password }}
                            </div>
                        </div>

                        <div class="mb-3">
                            <label for="password_confirmation" class="form-label">
                                Confirmer le mot de passe
                            </label>
                            <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-lock-fill"></i>
                </span>
                                <input
                                    id="password_confirmation"
                                    v-model="formData.password_confirmation"
                                    type="password"
                                    class="form-control"
                                    placeholder="Confirmez votre mot de passe"
                                />
                            </div>
                            <div v-if="validationErrors.password_confirmation" class="form-text text-danger">
                                {{ validationErrors.password_confirmation }}
                            </div>
                        </div>

                        <div class="d-grid gap-2 mt-4">
                            <button
                                @click="registerAndActivate"
                                :disabled="loading || !isFormValid"
                                class="btn btn-primary"
                            >
                <span v-if="loading">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Chargement...
                </span>
                                <span v-else>
                  <i class="bi bi-person-plus-fill me-2"></i>
                  Créer mon compte
                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Custom transitions */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>
